import React, { useMemo, useEffect } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useGetOwnersQuery } from '../../../store/integrations/integrations'
import { type IntegrationServiceId } from '../../../common/integrations'
import { getContactDropdownComponent } from '../ContactDropdownItems'
import { Owner } from '../../../models/User'

export interface OwnerDropdownProps {
  serviceId: IntegrationServiceId
  onOwnerOptionSelected?: (option: SelectOption) => void
  initialSelectedOwnerId?: string
  showLabel?: boolean
  isLoading?: boolean
}

type OwnerResponseItem = {
  owner: Owner[]
}

const getOwnerOptions = (items: OwnerResponseItem[] | undefined): SelectOption[] => {
  if (!items) {
    return []
  }

  return items.flatMap((item) =>
    (item.owner ?? []).map((owner) => ({
      id: owner.ownerId,
      label: owner.name,
    })),
  )
}

export const OwnerDropdown: React.FC<OwnerDropdownProps> = ({
  serviceId,
  onOwnerOptionSelected,
  initialSelectedOwnerId,
  showLabel = true,
  isLoading = false,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { data, isLoading: isOwnersLoading } = useGetOwnersQuery(
    {
      accountId,
      connector: serviceId,
    },
    { skip: !accountId || !serviceId },
  )

  const allOwnerOptions = useMemo(() => {
    return getOwnerOptions(data?.data as OwnerResponseItem[] | undefined)
  }, [data])

  const initialSelectedOption = useMemo(() => {
    if (!initialSelectedOwnerId) {
      return null
    }

    return allOwnerOptions.find((option) => option.id === initialSelectedOwnerId) ?? null
  }, [allOwnerOptions, initialSelectedOwnerId])

  useEffect(() => {
    if (initialSelectedOption) {
      onOwnerOptionSelected?.(initialSelectedOption)
    }
  }, [initialSelectedOption, onOwnerOptionSelected])

  return (
    <div>
      {showLabel && <div className='lead-extra-small'>Owner</div>}
      <SelectDropdown
        options={allOwnerOptions}
        initialSelectedOption={initialSelectedOption}
        className='py-2 zocks-px-0.75'
        onOptionSelected={(option: SelectOption) => {
          onOwnerOptionSelected?.(option)
        }}
        DropDownItemOptionComponent={getContactDropdownComponent(serviceId)}
        searchable
        placeholder='Select owner'
        loading={isOwnersLoading || isLoading}
      />
    </div>
  )
}
